import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ContentShelf from '../../components/responsive/ContentShelf';
import { ContentState } from '../../components/content/DiscoveryContent';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { useUserStore } from '../../stores/userStore';
import { trackBannerClick, trackBannerImpression } from '../../lib/gaEvents';
import { bannerApi } from '../../api/banner';
import { REFERRER_TYPE } from '../../constants/gaEnums';
import { useBanners } from './useBanners';
import { homeHeroBanners, bannerCopy, bannerDestination } from './homeBanners';
import HeroCreative from './HeroCreative';
import './HomeHero.css';
import baseline from '../../app-ui/baseline';

function HeroCard({ banner, loopDuplicate = false }) {
  const { locale } = useWebLocale();
  const user = useUserStore((state) => state.user);
  const ref = useRef(null);
  const delivered = useRef(false);
  const copy = bannerCopy(banner, locale);

  const destination = bannerDestination(banner, window.location.origin);
  const info = { bannerId: banner.id, bannerTitle: banner.title, bannerLocation: 'home_hero' };
  useEffect(() => {
    const node = ref.current;
    if (loopDuplicate || !node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (document.hidden || delivered.current) return;
        const visible = entries.some(
          (entry) =>
            entry.isIntersecting &&
            entry.boundingClientRect.width > 0 &&
            entry.boundingClientRect.height > 0 &&
            entry.intersectionRect.width / entry.boundingClientRect.width >= 0.5 &&
            entry.intersectionRect.height / entry.boundingClientRect.height >= 0.5,
        );
        if (visible) {
          delivered.current = true;
          trackBannerImpression(
            { bannerId: banner.id, bannerTitle: banner.title, bannerLocation: 'home_hero' },
            user,
          );
        }
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1], rootMargin: '-60px 0px -80px 0px' },
    );
    observer.observe(node);
    const resume = () => {
      if (!document.hidden) {
        observer.unobserve(node);
        observer.observe(node);
      }
    };
    document.addEventListener('visibilitychange', resume);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', resume);
    };
  }, [banner.id, banner.title, user, loopDuplicate]);
  const Wrapper = destination?.path ? Link : destination?.url ? 'a' : 'div';
  const navigation = destination?.path
    ? { to: destination.path, state: { referrerType: REFERRER_TYPE.BANNER } }
    : destination?.url
      ? { href: destination.url, target: '_blank', rel: 'noopener noreferrer' }
      : {};
  return (
    <div ref={ref} className="pp-home-hero-item" aria-hidden={loopDuplicate || undefined}>
      <Wrapper
        {...navigation}
        tabIndex={loopDuplicate ? -1 : undefined}
        className="pp-home-hero-card"
        aria-label={copy.headline || banner.title}
        onClick={
          destination
            ? () => {
                bannerApi.trackClick(banner.id).catch(() => {});
                trackBannerClick({ ...info, pageType: 'home' }, user);
              }
            : undefined
        }
      >
        <HeroCreative banner={banner} locale={locale} />
      </Wrapper>
    </div>
  );
}
export default function HomeHero() {
  const query = useBanners();
  const { locale } = useWebLocale();
  const items = homeHeroBanners(query.data);
  return (
    <ContentState query={query}>
      {items.length > 0 && (
        <ContentShelf
          profile="hero"
          loop
          navigation="arrows"
          autoAdvanceMs={baseline.heroAutoAdvanceMs}
          className={items.length === 1 ? 'pp-home-hero-single' : ''}
          label={locale === 'en' ? 'Featured stories and events' : '추천 소식과 이벤트'}
        >
          {items.map((banner) => (
            <HeroCard key={banner.id} banner={banner} />
          ))}
        </ContentShelf>
      )}
    </ContentState>
  );
}
